import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DefiDatagotchiCallout } from '@components/DefiDatagotchiCallout/DefiDatagotchiCallout'
import { EditionProgressBar } from '@components/Survey/TabbedPane/EditionProgressBar'
import { ConsentContext } from '@context/ConsentContext'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import './HomeDefiJourney.scss'

type MacroId = 'consent' | 'questionnaire' | 'simulator'
type MacroStatus = 'completed' | 'current' | 'upcoming'

type MacroStep = {
  id: MacroId
  status: MacroStatus
}

function buildMacroSteps(hasConsented: boolean, step: SurveyStep, started: boolean): MacroStep[] {
  if (!started) {
    return [
      { id: 'consent', status: 'upcoming' },
      { id: 'questionnaire', status: 'upcoming' },
      { id: 'simulator', status: 'upcoming' },
    ]
  }
  const atSimulator = step === SurveyStep.Sandbox
  const resultsDone =
    step === SurveyStep.Results ||
    step === SurveyStep.ResultsIntermediate ||
    atSimulator
  return [
    {
      id: 'consent',
      status: hasConsented ? 'completed' : 'current',
    },
    {
      id: 'questionnaire',
      status: !hasConsented ? 'upcoming' : resultsDone ? 'completed' : 'current',
    },
    {
      id: 'simulator',
      status: atSimulator ? 'current' : 'upcoming',
    },
  ]
}

type Props = {
  className?: string
}

export function HomeDefiJourney({ className }: Props) {
  const { t } = useTranslation('Home')
  const { hasConsented, consentData } = useContext(ConsentContext)
  const { step } = useContext(SurveyContext)
  const started = !!hasConsented || !!consentData
  const [parcoursOpen, setParcoursOpen] = useState(false)

  const macros = useMemo(
    () => buildMacroSteps(!!hasConsented, step, started),
    [hasConsented, step, started],
  )

  return (
    <DefiDatagotchiCallout className={['HomeDefiJourney', className].filter(Boolean).join(' ')}>
      {!started && (
        <>
          <p>{t('defiCallout.p1')}</p>
          <p>{t('defiCallout.p2')}</p>
          <button
            type="button"
            className="HomeDefiJourney-toggle"
            aria-expanded={parcoursOpen}
            onClick={() => setParcoursOpen((v) => !v)}>
            {parcoursOpen ? t('journey.toggleHide') : t('journey.toggleShow')}
          </button>
          {parcoursOpen && (
            <ol className="HomeDefiJourney-list">
              {macros.map((m) => (
                <li key={m.id} className={`HomeDefiJourney-list-item is-${m.status}`}>
                  <span className="HomeDefiJourney-list-label">{t(`journey.steps.${m.id}`)}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      {started && (
        <>
          <div className="HomeDefiJourney-progress">
            <EditionProgressBar />
          </div>
          <ol className="HomeDefiJourney-list">
            {macros.map((m) => (
              <li key={m.id} className={`HomeDefiJourney-list-item is-${m.status}`}>
                <span className="HomeDefiJourney-list-mark" aria-hidden>
                  {m.status === 'completed' ? '✓' : m.status === 'current' ? '●' : '○'}
                </span>
                <span className="HomeDefiJourney-list-label">{t(`journey.steps.${m.id}`)}</span>
                <span className="HomeDefiJourney-list-status">
                  {t(`journey.status.${m.status}`)}
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
    </DefiDatagotchiCallout>
  )
}

export default HomeDefiJourney
